import { useMemo, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import {
  AlertTriangle,
  Banknote,
  Calendar,
  ClipboardList,
  RefreshCw,
  Repeat,
  ShoppingCart,
  Smile,
  Ticket,
  UserPlus,
} from 'lucide-react';
import { Line, LineChart, ResponsiveContainer } from 'recharts';
import { fetchDashboardLive } from '@/lib/api/reports.js';
import { appointmentsApi, type AppointmentRow } from '@/lib/api/appointments.js';
import { formatCurrency, formatRelativeTime } from '@/lib/format.js';
import { useAuthStore } from '@/stores/auth.js';
import { cn } from '@/lib/utils.js';
import { wsManager } from '@/lib/websocket-manager.js';
import { usePermissions } from '@/hooks/usePermissions.js';
import { postLoginPath } from '@/lib/post-login-path.js';

const REFRESH_MS = 60_000;

function greetingForHour(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function LiveIndicator() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-600" />
      </span>
      Live
    </span>
  );
}

function GoalBar({ pct, goal }: { pct: number; goal: string }) {
  const g = Number(goal);
  if (!g || g <= 0) return null;
  return (
    <div className="mt-3">
      <div className="mb-1 flex items-center justify-between text-xs font-medium text-slate-500">
        <span>Goal: {formatCurrency(goal)}</span>
        <span className={pct >= 100 ? 'font-bold text-emerald-600' : ''}>{Math.round(pct)}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className={cn('h-full rounded-full', pct >= 100 ? 'bg-emerald-500' : 'bg-primary')}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}

function KpiCard({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon: typeof Ticket;
  children: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <p className="text-sm font-medium text-slate-500">{label}</p>
        <div className="rounded-xl bg-primary/10 p-2 text-primary">
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const { can, isLoading: permissionsLoading } = usePermissions();
  const now = new Date();
  const canViewDashboard = can('view_dashboard');

  useEffect(() => {
    if (!user?.storeId || !canViewDashboard) return;
    wsManager.connect(`store:${user.storeId}`, `/ws/store/${user.storeId}`);
    const unsub = wsManager.on(`store:${user.storeId}`, (data) => {
      if (
        data.type === 'payment.recorded' ||
        data.type === 'ticket.status_changed' ||
        data.type === 'queue.counts_updated'
      ) {
        queryClient.invalidateQueries({ queryKey: ['dashboard-live'] });
      }
    });
    return () => unsub();
  }, [user?.storeId, queryClient, canViewDashboard]);

  const q = useQuery({
    queryKey: ['dashboard-live'],
    queryFn: () => fetchDashboardLive(),
    refetchInterval: REFRESH_MS,
    enabled: !permissionsLoading && canViewDashboard,
  });

  const today = new Date().toISOString().split('T')[0]!;
  const aptQuery = useQuery({
    queryKey: ['appointments-today-dashboard', today],
    queryFn: () => appointmentsApi.list({ date: today }),
    refetchInterval: REFRESH_MS,
    enabled: !permissionsLoading && canViewDashboard,
  });

  const data = q.data;
  const chartData = useMemo(
    () =>
      (data?.last7Days ?? []).map((d) => ({
        date: d.date.slice(5),
        revenue: Number(d.revenue),
      })),
    [data?.last7Days],
  );

  if (!permissionsLoading && !canViewDashboard) {
    return <Navigate to={postLoginPath(user?.role ?? 'front_desk')} replace />;
  }

  if (q.isLoading && !data) {
    return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading dashboard…</div>;
  }

  if (q.isError || !data) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-red-700">
        Unable to load live dashboard. Apply migration 0025 and restart the API.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-950">
            {greetingForHour(now.getHours())}, {user?.firstName ?? 'there'}!
          </h1>
          <p className="text-sm text-slate-500">
            {now.toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LiveIndicator />
          <span className="text-xs text-slate-500">
            Updated {formatRelativeTime(data.generatedAt)}
          </span>
          <button
            type="button"
            className="rounded-lg border border-slate-300 p-2 hover:bg-slate-50"
            onClick={() => void q.refetch()}
            title="Refresh"
          >
            <RefreshCw className={cn('h-4 w-4', q.isFetching && 'animate-spin')} />
          </button>
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Revenue Today" icon={Banknote}>
          <p className="text-3xl font-bold text-slate-950">{formatCurrency(data.today.revenue)}</p>
          <GoalBar pct={data.goals.daily.pct} goal={data.goals.daily.goal} />
        </KpiCard>
        <Link to="/reports/outstanding-balances" className="block rounded-2xl transition hover:ring-2 hover:ring-primary/30">
          <KpiCard label="Outstanding balances" icon={AlertTriangle}>
            <p className="text-3xl font-bold text-slate-950">
              {formatCurrency(data.outstanding?.total ?? '0')}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              {data.outstanding?.count ?? 0} unpaid · open report →
            </p>
          </KpiCard>
        </Link>
        <KpiCard label="Tickets" icon={Ticket}>
          <p className="text-3xl font-bold text-slate-950">{data.today.ticketsCreated} opened</p>
          <p className="mt-1 text-sm text-slate-600">
            {data.today.ticketsClosed} closed · {data.queue.overdue + data.queue.dueSoon + data.queue.ready}{' '}
            active in queue
          </p>
        </KpiCard>
        <KpiCard label="Cash Drawer" icon={Banknote}>
          <p className="text-3xl font-bold text-slate-950">
            {data.cashSession.expectedCash
              ? formatCurrency(data.cashSession.expectedCash)
              : '—'}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            {data.cashSession.isOpen
              ? `Open${data.cashSession.openedAt ? ` since ${formatRelativeTime(data.cashSession.openedAt)}` : ''}`
              : 'No open session'}
          </p>
        </KpiCard>
        <KpiCard label="Today's NPS" icon={Smile}>
          <p className="text-3xl font-bold text-slate-950">
            {data.today.nps.avg != null ? data.today.nps.avg.toFixed(1) : '—'}
            <span className="text-lg font-medium text-slate-400"> / 10</span>
          </p>
          <p className="mt-1 text-sm text-slate-600">{data.today.nps.count} responses</p>
        </KpiCard>
        {data.subscriptions ? (
          <>
            <KpiCard label="Subscription MRR" icon={Repeat}>
              <p className="text-3xl font-bold text-slate-950">
                {formatCurrency(data.subscriptions.mrr)}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {data.subscriptions.active} active subscriptions
              </p>
            </KpiCard>
            <KpiCard label="Subs Due Today" icon={Repeat}>
              <p className="text-3xl font-bold text-slate-950">{data.subscriptions.dueToday}</p>
              <Link to="/subscriptions" className="mt-1 inline-block text-sm font-semibold text-primary hover:underline">
                View subscriptions →
              </Link>
            </KpiCard>
          </>
        ) : null}
      </div>

      {(aptQuery.data?.length ?? 0) > 0 ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-bold text-slate-950">
              <Calendar className="h-5 w-5 text-primary" />
              Today&apos;s Appointments ({aptQuery.data?.length ?? 0})
            </h2>
            <Link to="/appointments" className="text-sm font-semibold text-primary hover:underline">
              View all →
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {(aptQuery.data ?? [])
              .filter((a) => !['cancelled', 'completed', 'no_show'].includes(a.status))
              .slice(0, 8)
              .map((apt) => (
                <DashboardAppointmentRow key={apt.id} apt={apt} />
              ))}
          </div>
        </section>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[2fr_2fr_1.4fr]">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-bold text-slate-950">Ticket Queue</h2>
            <Link to="/tickets" className="text-sm font-semibold text-primary hover:underline">
              View all →
            </Link>
          </div>
          <QueueBlock
            title={`🔴 Overdue (${data.queue.overdue})`}
            tickets={data.queue.overdueTickets}
            tone="red"
          />
          <QueueBlock
            title={`🟠 Due soon (${data.queue.dueSoon})`}
            tickets={data.queue.dueSoonTickets}
            tone="amber"
          />
          <QueueBlock
            title={`✅ Ready (${data.queue.ready})`}
            tickets={data.queue.readyTickets.map((t) => ({
              id: t.id,
              ticketNumber: t.ticketNumber,
              promisedDate: t.readyAt,
            }))}
            tone="green"
          />
        </section>

        <section className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 font-bold text-slate-950">Team Status</h2>
            {data.team.length === 0 ? (
              <p className="text-sm text-slate-500">No active timers right now.</p>
            ) : (
              <ul className="space-y-3">
                {data.team.map((m) => (
                  <li key={`${m.userId}-${m.ticketId}`} className="rounded-xl bg-slate-50 p-3 text-sm">
                    <p className="font-semibold text-slate-950">🟢 {m.name}</p>
                    <p className="text-slate-600">
                      Working on{' '}
                      <Link className="font-semibold text-primary" to={`/tickets/${m.ticketId}`}>
                        {m.ticketNumber}
                      </Link>
                    </p>
                    {m.startedAt ? (
                      <p className="text-xs text-slate-500">Since {formatRelativeTime(m.startedAt)}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-2 font-bold text-slate-950">Last 7 Days</h2>
            <div className="h-24">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <Line type="monotone" dataKey="revenue" stroke="#1B4F8A" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 font-bold text-slate-950">
              <AlertTriangle className="h-4 w-4 text-amber-500" /> Needs Attention
            </h2>
            {data.alerts.length === 0 ? (
              <p className="text-sm text-slate-500">All clear.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.alerts.map((a, i) => (
                  <li key={`${a.type}-${i}`}>
                    {a.link ? (
                      <Link to={a.link} className="font-medium text-slate-800 hover:text-primary">
                        {a.message}
                      </Link>
                    ) : (
                      <span className="text-slate-700">{a.message}</span>
                    )}
                  </li>
                ))}
                {data.subscriptions && data.subscriptions.dueToday > 0 ? (
                  <li>
                    <Link to="/subscriptions" className="font-medium text-amber-800 hover:text-primary">
                      {data.subscriptions.dueToday} subscription
                      {data.subscriptions.dueToday === 1 ? '' : 's'} due for payment today
                    </Link>
                  </li>
                ) : null}
                {data.subscriptions && data.subscriptions.pastDue > 0 ? (
                  <li>
                    <Link to="/subscriptions?tab=past_due" className="font-medium text-red-800 hover:text-primary">
                      {data.subscriptions.pastDue} past due subscription
                      {data.subscriptions.pastDue === 1 ? '' : 's'}
                    </Link>
                  </li>
                ) : null}
              </ul>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-2 font-bold text-slate-950">Revenue by Type</h2>
            <ul className="space-y-1 text-sm">
              {data.revenueBySource.map((r) => (
                <li key={r.source} className="flex justify-between">
                  <span className="capitalize text-slate-600">{r.source}</span>
                  <span className="font-semibold">
                    {formatCurrency(r.amount)} ({r.pct}%)
                  </span>
                </li>
              ))}
            </ul>
            <h3 className="mb-1 mt-4 text-xs font-semibold uppercase text-slate-500">Payments</h3>
            <ul className="space-y-1 text-sm">
              <li className="flex justify-between">
                <span>Cash</span>
                <span className="font-semibold">{formatCurrency(data.paymentMethods.cash)}</span>
              </li>
              <li className="flex justify-between">
                <span>Card</span>
                <span className="font-semibold">{formatCurrency(data.paymentMethods.card)}</span>
              </li>
              <li className="flex justify-between">
                <span>Store credit</span>
                <span className="font-semibold">{formatCurrency(data.paymentMethods.storeCredit)}</span>
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="mb-2 font-bold text-slate-950">Quick Actions</h2>
            <div className="grid gap-2">
              <QuickLink to="/tickets/quick" icon={Ticket} label="New Quick Ticket" />
              <QuickLink to="/pos" icon={ShoppingCart} label="POS Sale" />
              <QuickLink to="/inquiries/new" icon={ClipboardList} label="New Inquiry" />
              <QuickLink to="/customers" icon={UserPlus} label="Customers" />
            </div>
          </div>
        </section>
      </div>

      <footer className="flex flex-wrap gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm text-slate-700 shadow-sm">
        <span>
          This week: <strong>{formatCurrency(data.goals.weekly.actual)}</strong> (
          {Math.round(data.goals.weekly.pct)}% of goal)
        </span>
        <span>
          This month: <strong>{formatCurrency(data.goals.monthly.actual)}</strong> (
          {Math.round(data.goals.monthly.pct)}% of goal)
        </span>
        <span>
          Avg ticket today: <strong>{formatCurrency(data.today.avgTicketValue)}</strong>
        </span>
      </footer>
    </div>
  );
}

function QueueBlock({
  title,
  tickets,
  tone,
}: {
  title: string;
  tickets: Array<{ id: string; ticketNumber: string; promisedDate?: string | null }>;
  tone: 'red' | 'amber' | 'green';
}) {
  const border =
    tone === 'red'
      ? 'border-red-100 bg-red-50/50'
      : tone === 'amber'
        ? 'border-amber-100 bg-amber-50/40'
        : 'border-emerald-100 bg-emerald-50/40';
  return (
    <div className={cn('mb-3 rounded-xl border p-3 last:mb-0', border)}>
      <p className="text-xs font-bold uppercase tracking-wide text-slate-700">{title}</p>
      {tickets.length === 0 ? (
        <p className="mt-1 text-xs text-slate-500">None</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {tickets.slice(0, 5).map((t) => (
            <li key={t.id}>
              <Link to={`/tickets/${t.id}`} className="text-sm font-semibold text-primary hover:underline">
                {t.ticketNumber}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function QuickLink({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: typeof Ticket;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
    >
      <Icon className="h-4 w-4 text-primary" />
      {label}
    </Link>
  );
}

function DashboardAppointmentRow({ apt }: { apt: AppointmentRow }) {
  const time = apt.startTime ? formatAptTime(String(apt.startTime).slice(0, 5)) : '—';
  const services = (apt.servicesRequested ?? []).map((s) => s.serviceName).join(' + ') || 'Services';
  return (
    <div className="flex flex-wrap items-center gap-3 py-2.5">
      <span className="w-20 text-sm font-semibold text-slate-700">{time}</span>
      <span className="min-w-0 flex-1 text-sm">
        <span className="font-semibold text-slate-900">{apt.customerName}</span>
        <span className="text-slate-500"> · {services}</span>
      </span>
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs capitalize text-slate-600">
        {apt.status.replace('_', ' ')}
      </span>
    </div>
  );
}

function formatAptTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ampm = (h ?? 0) >= 12 ? 'PM' : 'AM';
  const hr = (h ?? 0) % 12 || 12;
  return `${hr}:${String(m ?? 0).padStart(2, '0')} ${ampm}`;
}
